import React, { useEffect, useState } from "react";
import api, { eur, fmtDate, STATUS_LABELS, STATUS_STYLES } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { MapPin, Package, CheckCircle2, ChevronLeft, ChevronRight, Truck, Navigation } from "lucide-react";
import { toast } from "sonner";

const iso = (d) => d.toISOString().slice(0, 10);

export default function TransporteurView() {
  const [day, setDay] = useState(new Date());
  const [stops, setStops] = useState([]);

  const load = () => {
    api.get(`/route-sheet?date=${iso(day)}`).then((r) => setStops(r.data)).catch(() => {});
  };
  useEffect(load, [day]);

  const shift = (n) => {
    const d = new Date(day);
    d.setDate(d.getDate() + n);
    setDay(d);
  };

  const deliver = async (o) => {
    await api.patch(`/orders/${o.id}/deliver`);
    toast.success(`${o.client_name} — livré`);
    load();
  };

  const done = stops.filter((s) => s.status === "livree").length;
  const totalItems = stops.reduce((s, o) => s + o.items.reduce((a, b) => a + b.quantity, 0), 0);

  return (
    <div data-testid="transporteur-view" className="mx-auto max-w-2xl">
      <div className="mb-4">
        <p className="text-xs uppercase tracking-[0.2em] text-secondary">Feuille de route</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Tournée transporteur</h1>
      </div>

      {/* Date navigator */}
      <Card className="mb-4 flex items-center justify-between p-3">
        <Button size="icon" variant="outline" onClick={() => shift(-1)} data-testid="prev-day-btn"><ChevronLeft className="h-5 w-5" /></Button>
        <div className="text-center">
          <p className="font-display text-lg font-bold capitalize">
            {day.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          <button className="text-xs text-secondary underline" onClick={() => setDay(new Date())} data-testid="today-btn">Aujourd'hui</button>
        </div>
        <Button size="icon" variant="outline" onClick={() => shift(1)} data-testid="next-day-btn"><ChevronRight className="h-5 w-5" /></Button>
      </Card>

      {/* Summary */}
      <div className="mb-4 grid grid-cols-3 gap-3">
        <Card className="p-3 text-center">
          <Truck className="mx-auto h-5 w-5 text-secondary" />
          <p className="tabular mt-1 text-xl font-bold">{stops.length}</p>
          <p className="text-[11px] uppercase text-muted-foreground">Arrêts</p>
        </Card>
        <Card className="p-3 text-center">
          <Package className="mx-auto h-5 w-5 text-primary" />
          <p className="tabular mt-1 text-xl font-bold">{totalItems}</p>
          <p className="text-[11px] uppercase text-muted-foreground">Colis</p>
        </Card>
        <Card className="p-3 text-center">
          <CheckCircle2 className="mx-auto h-5 w-5 text-primary" />
          <p className="tabular mt-1 text-xl font-bold">{done}/{stops.length}</p>
          <p className="text-[11px] uppercase text-muted-foreground">Livrés</p>
        </Card>
      </div>

      {/* Stops */}
      <div className="space-y-3">
        {stops.length === 0 && (
          <Card className="p-8 text-center text-muted-foreground">Aucune livraison planifiée pour ce jour.</Card>
        )}
        {stops.map((o, i) => {
          const delivered = o.status === "livree";
          return (
            <Card key={o.id} data-testid={`stop-${o.id}`} className={`overflow-hidden border-l-4 ${delivered ? "border-l-primary opacity-70" : "border-l-secondary"}`}>
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full font-display font-bold ${delivered ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
                      {i + 1}
                    </div>
                    <div>
                      <p className="font-display text-lg font-bold leading-tight">{o.client_name}</p>
                      <p className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" /> {o.client_address}, {o.client_city}
                      </p>
                      <p className="tabular text-xs text-muted-foreground">{o.order_number}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={STATUS_STYLES[o.status]}>{STATUS_LABELS[o.status]}</Badge>
                </div>

                <div className="mt-3 rounded-md bg-muted/50 p-3">
                  {o.items.map((it) => (
                    <div key={it.product_id} className="flex justify-between py-0.5 text-sm">
                      <span>{it.name}</span>
                      <span className="tabular font-medium">×{it.quantity}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((o.client_address || "") + " " + (o.client_city || ""))}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-border py-2.5 text-sm font-medium hover:bg-muted"
                    data-testid={`navigate-${o.id}`}
                  >
                    <Navigation className="h-4 w-4" /> Itinéraire
                  </a>
                  <Button
                    className="flex-1"
                    size="lg"
                    disabled={delivered}
                    onClick={() => deliver(o)}
                    data-testid={`deliver-btn-${o.id}`}
                  >
                    <CheckCircle2 className="mr-1.5 h-5 w-5" /> {delivered ? "Livré" : "Marquer livré"}
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
